import React from 'react';
import { InteractiveContentCard, SuCarousel } from '@amzn/seller-university-ui-components';
import { useBundle } from '@amzn/react-arb-tools';
import { t } from 'src/utils/translation';
import { BUNDLE_NAME, LEARNING_PATHWAY_STRING_ID } from 'src/constants/localization';
import { LearningPathDetailData } from 'src/types/state/learningPathway';
import useUserMetrics from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';

interface LearningPathwayCardProps {
  id: string;
  title: string;
  previewDescription: string;
  imageSrc: string;
  contentVariant: 'Video';
  stats: {
    totalSections: number;
    totalModules: number;
    totalDuration: number;
    totalDocuments: number;
    totalVideos: number;
  };
  onClick: () => void;
}

interface LearningPathwaysCarouselProps {
  pathways: LearningPathwayCardProps[];
  isPolyD?: boolean;
}

/**
 * Displays a carousel of learning pathway cards with 2 cards per slide
 */
const LearningPathwaysCarousel: React.FC<LearningPathwaysCarouselProps> = ({ pathways, isPolyD }) => {
  const [learningPathwayBundle] = useBundle(BUNDLE_NAME.LEARNING_PATHWAY);
  const { clickMetric } = useUserMetrics();

  const formatLearningPathDetails = (statsData: LearningPathDetailData) => {
    return {
      learningPathType: t(
        learningPathwayBundle,
        LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_PAGE_HEADER_PATH_TYPE,
      ),
      totalSections: t(
        learningPathwayBundle,
        LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_TOTAL_SECTIONS,
        { count: statsData.totalSections },
      ),
      totalModules: t(
        learningPathwayBundle,
        LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_TOTAL_MODULES,
        { count: statsData.totalModules ?? (statsData.totalDocuments + statsData.totalVideos) },
      ),
      duration: t(
        learningPathwayBundle,
        LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_DURATION,
        {
          minutes: Math.round((statsData.totalDuration || 0) / 60),
        },
      ),
    };
  };

  return (
    <SuCarousel
      items={pathways}
      getItemKey={(pathway) => pathway.id}
      renderItem={(pathway) => {
        const formattedDetails = formatLearningPathDetails({
          totalSections: pathway.stats.totalSections,
          totalDocuments: pathway.stats.totalDocuments,
          totalVideos: pathway.stats.totalVideos,
          totalDuration: pathway.stats.totalDuration,
        });

        return (
          <InteractiveContentCard
            layout="vertical"
            title={isPolyD ? pathway.title : t(
              learningPathwayBundle,
              pathway.title,
            )}
            previewDescription={isPolyD ? pathway.previewDescription : t(
              learningPathwayBundle,
              pathway.previewDescription,
            )}
            imageSrc={pathway.imageSrc}
            contentVariant={pathway.contentVariant}
            metadata={[
              pathway.stats.totalDuration ? formattedDetails.duration : null,
            ].filter(Boolean) as string[]}
            onClick={() => {
              clickMetric({
                feature: FeatureName.LearningPathwayPage,
                metadata: {
                  url: window.location.href,
                  pathwayId: pathway.id,
                  pathwayTitle: pathway.title,
                },
              });
              pathway.onClick();
            }}
            className="h-full"
          />
        );
      }}
      layout={{
        xsmall: { cols: 1 },
        small: { cols: 1 },
        medium: { cols: 2 },
        large: { cols: 2 },
      }}
      className="mx-[-40px] px-su-space-06"
    />
  );
};

export default LearningPathwaysCarousel;
